import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import {
  NotesActions,
  useTimeSignalActions
} from 'modules/notes/notesSelector/hooks';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';

export const useBulkActionCallback = ({
  notes
}: {
  notes: TimeSignal[];
}): {
  bulkActionCallback: (classificationTarget: NotesActions) => void;
  isActionInProgress: boolean;
  filteredNotes: TimeSignal[];
} => {
  const [saving, setSaving] = useState(false);
  const { resetForm } = useNotesSelectionReset();
  const { handleTimeSignalAction, isActionInProgress } = useTimeSignalActions();
  const { control } = useFormContext();
  const values = useWatch({ control });

  const filteredNotes = useMemo(() => {
    return notes.filter((note) => values[`${note.date}_${note.id}`]);
  }, [notes, values]);

  const bulkActionCallback = useCallback(
    async (classificationTarget: NotesActions) => {
      const differentNotes = filteredNotes.filter(
        (note) =>
          classificationTarget === 'DeleteForever' ||
          note.classification !== TimeSignalClassification[classificationTarget]
      );

      setSaving(true);

      if (differentNotes.length) {
        await handleTimeSignalAction({
          classificationTarget,
          selectedNotes: differentNotes.map((note) => note.id),
          isAllSelected: false,
          unSelectedNotes: [],
          note: differentNotes.length === 1 ? differentNotes[0] : undefined
        });
      }

      resetForm();
      setSaving(false);
    },
    [filteredNotes, handleTimeSignalAction, resetForm]
  );

  return {
    bulkActionCallback,
    isActionInProgress: isActionInProgress || saving,
    filteredNotes
  };
};
